import { Button, Modal } from "src/components";

// M10-15822
// Shown when OMS refuses a line mapped to "Others" because this company's own product master
// holds an active product whose brand appears in the invoice line description — i.e. the line
// is one of our own products, not another company's.
//
// Deliberately has no Proceed/Confirm action: the block is absolute, and the only way forward is
// to map the line to the correct product. Rendered from two places (the mapping modal and the
// Confirm Invoice screen) so both paths give the operator the identical explanation.
export interface BrandConflictMatch {
  item_code: string;
  item_name: string;
  brand: string;
}

interface BrandConflictModalProps {
  isOpen: boolean;
  onClose: () => void;
  matches: BrandConflictMatch[];
  description?: string;
}

const BrandConflictModal = ({ isOpen, onClose, matches, description }: BrandConflictModalProps) => {
  return (
    // zIndex must clear the parent dialog: the mapping modal already renders this component from
    // inside a headlessui Dialog and Modal's own default is 10, so without this the block renders
    // behind the modal that triggered it. backdropClose stays at its false default — a hard block
    // must not be dismissable by clicking away.
    <Modal isOpen={isOpen} isHeader={false} onCloseModal={onClose} zIndex={50}>
      <div className="flex flex-col gap-y-2 mb-4 w-[760px] mx-auto px-6 bg-white p-4 rounded-xl">
        <p className="text-lg font-semibold text-gray-800">Cannot map to Others</p>
        <p className="text-sm text-gray-600 mt-2">
          This description matches an existing product in this company&apos;s product master, so it cannot be
          mapped to Others. Map it to the correct product instead.
        </p>
        {description ? (
          <p className="text-sm text-gray-500 mt-1">
            Invoice description: <span className="font-medium text-gray-800">{description}</span>
          </p>
        ) : null}
        <div className="mt-3 max-h-[240px] overflow-y-auto border border-gray-200 rounded-lg">
          <table className="w-full text-sm text-left">
            <thead className="bg-[#F1F1F1] sticky top-0">
              <tr>
                <th className="p-2 border-b border-gray-200">Item Code</th>
                <th className="p-2 border-b border-gray-200">Item Name</th>
                <th className="p-2 border-b border-gray-200">Brand</th>
              </tr>
            </thead>
            <tbody>
              {matches.map((match, index) => (
                <tr key={`${match.item_code}-${index}`}>
                  <td className="p-2 border-b border-gray-100 font-medium text-gray-800">{match.item_code}</td>
                  <td className="p-2 border-b border-gray-100 text-gray-700">{match.item_name}</td>
                  <td className="p-2 border-b border-gray-100 text-gray-700">{match.brand}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button className="text-white bg-primary px-4 py-2 rounded-lg" onClick={onClose}>
            OK
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default BrandConflictModal;
